import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AXLE_PRESETS,
  brakePositions,
  hasCapability,
  wheelPositions,
  type AxleConfig,
} from '@prooffleet/contracts';
import { IconPlus } from '@tabler/icons-react';
import { ApiError, api, useSession } from '@prooffleet/client';
import { EmptyState, PageHeader, TableSkeleton } from '../components/Page.js';

interface UnitRow {
  _id: string;
  unitNumber: string;
  kind: string;
  vin: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  siteId: string | null;
  status: string;
  qrToken: string;
  axleConfig: AxleConfig;
  legalHold: { active: boolean };
}

interface SiteRow {
  _id: string;
  name: string;
}

const PRESET_OPTIONS = Object.entries(AXLE_PRESETS).map(([value, preset]) => ({
  value,
  label: preset.label,
}));

/**
 * Парк.
 *
 * Конфигурация осей выбирается пресетом, а не собирается по одной оси: реальный
 * запрос звучит как «ещё один Cascadia», а не «три оси, на первой одно колесо
 * с каждой стороны». Из пресета детерминированно выводятся позиции колёс и
 * тормозов, а из них — чек-лист под конкретную машину.
 */
export function Units(): React.JSX.Element {
  const actor = useSession((s) => s.actor);
  const canWrite = actor !== null && hasCapability(actor, 'unit:write');
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<UnitRow | null>(null);
  const [unitNumber, setUnitNumber] = useState('');
  const [preset, setPreset] = useState<string | null>('tractor_6x4');
  const [vin, setVin] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState<number | string>('');
  const [siteId, setSiteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['units'],
    queryFn: () => api<{ units: UnitRow[] }>('/units'),
  });
  const { data: siteData } = useQuery({
    queryKey: ['sites'],
    queryFn: () => api<{ sites: SiteRow[] }>('/sites'),
  });

  const units = data?.units ?? [];
  const siteName = new Map((siteData?.sites ?? []).map((s) => [s._id, s.name]));

  const reset = (): void => {
    setEditing(null);
    setUnitNumber('');
    setPreset('tractor_6x4');
    setVin('');
    setMake('');
    setModel('');
    setYear('');
    setSiteId(null);
    setError(null);
  };

  const save = useMutation({
    mutationFn: async () => {
      const chosen = preset === null ? undefined : AXLE_PRESETS[preset];
      const common = {
        vin: vin === '' ? null : vin.toUpperCase(),
        make: make === '' ? null : make,
        model: model === '' ? null : model,
        year: year === '' ? null : Number(year),
        siteId,
      };
      if (editing !== null) return api(`/units/${editing._id}`, { method: 'PATCH', body: common });
      if (chosen === undefined) throw new Error('preset missing');
      return api('/units', {
        method: 'POST',
        body: { ...common, unitNumber, kind: chosen.kind, axleConfig: chosen.config },
      });
    },
    onSuccess: async () => {
      setOpen(false);
      reset();
      await queryClient.invalidateQueries({ queryKey: ['units'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Could not save the unit');
    },
  });

  const rotateQr = useMutation({
    mutationFn: (id: string) => api(`/units/${id}/qr/rotate`, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['units'] });
    },
  });

  const previewConfig = preset === null ? null : (AXLE_PRESETS[preset]?.config ?? null);

  return (
    <Stack gap={0}>
      <PageHeader
        title="Units"
        count={isLoading ? undefined : units.length}
        hint="Tractors and trailers. The axle configuration decides which wheels and brakes the driver is asked to measure."
        action={
          canWrite ? (
            <Button
              leftSection={<IconPlus size={15} stroke={1.8} />}
              onClick={() => {
                reset();
                setOpen(true);
              }}
            >
              Add unit
            </Button>
          ) : undefined
        }
      />

      {isLoading && <TableSkeleton rows={4} cols={5} />}

      {!isLoading && units.length === 0 && (
        <EmptyState
          title="No units yet"
          hint="Add a tractor or trailer to generate its checklist and QR sticker."
        />
      )}

      {!isLoading && units.length > 0 && (
        <Table.ScrollContainer minWidth={820}>
          <Table className="pf-table" layout="fixed">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w="18%">Unit</Table.Th>
                <Table.Th w="22%">Vehicle</Table.Th>
                <Table.Th w="16%">Site</Table.Th>
                <Table.Th w="16%">Wheels / brakes</Table.Th>
                <Table.Th w="16%">QR token</Table.Th>
                <Table.Th w="12%" />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {units.map((unit) => (
                <Table.Tr key={unit._id}>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Link
                        to={`/units/${unit._id}`}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        <Text fw={600} fz="sm">
                          {unit.unitNumber}
                        </Text>
                      </Link>
                      {unit.legalHold.active && (
                        <Badge color="red.8" variant="filled" size="xs">
                          hold
                        </Badge>
                      )}
                    </Group>
                    <Text className="pf-eyebrow" mt={2}>
                      {unit.kind}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz="sm" c="gray.7" truncate>
                      {[unit.make, unit.model, unit.year].filter(Boolean).join(' ') || '—'}
                    </Text>
                    {unit.vin !== null && <Text className="pf-mono">{unit.vin}</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Text fz="sm" c="gray.7">
                      {unit.siteId === null ? '—' : (siteName.get(unit.siteId) ?? '—')}
                    </Text>
                  </Table.Td>
                  <Table.Td className="pf-num">
                    <Text fz="sm">
                      {wheelPositions(unit.axleConfig).length} / {brakePositions(unit.axleConfig).length}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text className="pf-mono" truncate>
                      {unit.qrToken}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Group gap={4} justify="flex-end" wrap="nowrap" className="pf-row-action">
                      <Button
                        size="compact-xs"
                        variant="default"
                        disabled={!canWrite}
                        onClick={() => {
                          setEditing(unit);
                          setUnitNumber(unit.unitNumber);
                          setVin(unit.vin ?? '');
                          setMake(unit.make ?? '');
                          setModel(unit.model ?? '');
                          setYear(unit.year ?? '');
                          setSiteId(unit.siteId);
                          setError(null);
                          setOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="gray"
                        disabled={!canWrite}
                        loading={rotateQr.isPending}
                        onClick={() => rotateQr.mutate(unit._id)}
                      >
                        New QR
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <Modal
        opened={open}
        onClose={() => setOpen(false)}
        title={editing === null ? 'New unit' : `Edit ${editing.unitNumber}`}
      >
        <Stack gap="md">
          {editing === null && (
            <>
              <TextInput
                label="Unit number"
                description="Fleet identifier painted on the door"
                value={unitNumber}
                onChange={(e) => setUnitNumber(e.currentTarget.value.toUpperCase())}
                placeholder="T-4473"
              />
              <Select
                label="Axle configuration"
                description="Decides the wheel and brake positions on the checklist"
                data={PRESET_OPTIONS}
                value={preset}
                onChange={setPreset}
                allowDeselect={false}
              />
              {previewConfig !== null && (
                <Alert color="blue">
                  <Text fz="sm">
                    {wheelPositions(previewConfig).length} wheel positions ·{' '}
                    {brakePositions(previewConfig).length} brake positions
                  </Text>
                  <Text fz="xs" c="dimmed" mt={2}>
                    The steer axle carries a 4/32&quot; tread limit, the others 2/32&quot;.
                  </Text>
                </Alert>
              )}
            </>
          )}

          <Group grow>
            <TextInput label="Make" value={make} onChange={(e) => setMake(e.currentTarget.value)} />
            <TextInput label="Model" value={model} onChange={(e) => setModel(e.currentTarget.value)} />
          </Group>
          <Group grow>
            <NumberInput label="Year" value={year} onChange={setYear} min={1980} max={2100} />
            <Select
              label="Site"
              placeholder="Unassigned"
              data={(siteData?.sites ?? []).map((s) => ({ value: s._id, label: s.name }))}
              value={siteId}
              onChange={setSiteId}
              clearable
            />
          </Group>
          <TextInput
            label="VIN"
            description="17 characters, optional"
            value={vin}
            onChange={(e) => setVin(e.currentTarget.value.toUpperCase())}
          />

          {error !== null && <Alert color="red">{error}</Alert>}

          <Button
            loading={save.isPending}
            disabled={editing === null && (unitNumber.trim() === '' || preset === null)}
            onClick={() => save.mutate()}
          >
            {editing === null ? 'Create unit' : 'Save'}
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
